// src/pages/admin/AdminLayout.jsx
import React, { useState } from "react";
import { Routes, Route, Navigate, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { styled, alpha, useTheme } from "@mui/material/styles";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";

import AccessRequestsPanel from "./AccessRequestsPanel";
import MarketsPanel from "./MarketsPanel";
import OrganizationsPanel from "./OrganizationsPanel";
import StripeManagement from "./StripeManagement";
import DirectRegisterPanel from "./DirectRegisterPanel";
import { clearAuth } from "../../utils/auth";

const SIDEBAR_WIDTH = 260;

const SidebarRoot = styled(Box)(({ theme }) => ({
  width: SIDEBAR_WIDTH,
  flexShrink: 0,
  height: "100vh",
  position: "sticky",
  top: 0,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 0 8px rgba(0,0,0,0.04)",
  display: "flex",
  flexDirection: "column",
}));

const AdminNavLink = ({ to, icon, label, end, onNavigate }) => {
  const location = useLocation();
  const isActive = end
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <ListItemButton
      component={NavLink}
      to={to}
      onClick={onNavigate}
      sx={(theme) => ({
        mx: 1,
        borderRadius: 1.5,
        mb: 0.25,
        "& .MuiListItemIcon-root": {
          minWidth: 36,
          color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
        },
        ...(isActive
          ? {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              fontWeight: 600,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              pl: "12px",
            }
          : {
              color: theme.palette.text.primary,
              "&:hover": { backgroundColor: alpha(theme.palette.action.hover, 0.08) },
            }),
      })}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{ fontWeight: isActive ? 600 : 500, fontSize: 14 }}
      />
    </ListItemButton>
  );
};

function SidebarContent({ onNavigate, navigate }) {
  return (
    <>
      {/* Brand */}
      <Box sx={{ p: "18px 16px 12px", display: "flex", alignItems: "center", gap: 1 }}>
        <AdminPanelSettingsRoundedIcon color="primary" />
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Admin Panel
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            AgentBlayna
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }} />

      <Typography
        sx={{
          textTransform: "uppercase",
          letterSpacing: ".08em",
          fontSize: 12,
          color: "text.secondary",
          px: 2,
          py: 0.5,
        }}
      >
        Management
      </Typography>

      <List sx={{ px: 0.5 }}>
        <AdminNavLink to="/admin" icon={<PeopleAltRoundedIcon />} label="Access Requests" end onNavigate={onNavigate} />
        <AdminNavLink to="/admin/markets" icon={<MapRoundedIcon />} label="Markets" onNavigate={onNavigate} />
        <AdminNavLink to="/admin/organizations" icon={<BusinessRoundedIcon />} label="Organizations" onNavigate={onNavigate} />
        <AdminNavLink to="/admin/stripe" icon={<PaymentRoundedIcon />} label="Stripe" onNavigate={onNavigate} />
        <AdminNavLink to="/admin/direct-register" icon={<PersonAddRoundedIcon />} label="Direct Register" onNavigate={onNavigate} />
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Divider />

      <List sx={{ px: 0.5 }}>
        <ListItemButton
          onClick={() => { clearAuth(); navigate("/login"); }}
          sx={(theme) => ({
            mx: 1,
            borderRadius: 1.5,
            mb: 0.5,
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.08),
              color: theme.palette.error.main,
              "& .MuiListItemIcon-root": { color: theme.palette.error.main },
            },
          })}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <LogoutRoundedIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontWeight: 500, fontSize: 14 }}
          />
        </ListItemButton>
      </List>

      <Box sx={{ p: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          &copy; {new Date().getFullYear()} AgentBlayna Admin
        </Typography>
      </Box>
    </>
  );
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerClose = () => setMobileOpen(false);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile AppBar */}
      {!isDesktop && (
        <AppBar
          position="fixed"
          color="inherit"
          elevation={1}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Toolbar>
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
              <MenuRoundedIcon />
            </IconButton>
            <AdminPanelSettingsRoundedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight={800} noWrap>
              Admin Panel
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Mobile Drawer */}
      {!isDesktop && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{ "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH, boxSizing: "border-box" } }}
        >
          <SidebarContent onNavigate={handleDrawerClose} navigate={navigate} />
        </Drawer>
      )}

      {/* Desktop Sidebar */}
      {isDesktop && (
        <SidebarRoot>
          <SidebarContent navigate={navigate} />
        </SidebarRoot>
      )}

      {/* Content Area */}
      <Box
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          pt: { xs: 2, md: 3 },
          mt: { xs: "64px", md: 0 },
          bgcolor: "background.default",
          overflow: "auto",
        }}
      >
        <Routes>
          <Route index element={<AccessRequestsPanel />} />
          <Route path="markets" element={<MarketsPanel />} />
          <Route path="organizations" element={<OrganizationsPanel />} />
          <Route path="stripe" element={<StripeManagement />} />
          <Route path="direct-register" element={<DirectRegisterPanel />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}
